// `PrintPreflight` and `PrintPlatform` (SuitePreflight/PrintPreflight.swift): layout checks on a print interior
// (cross-platform spec §9.1) with copyfitting suggestions (§9.2), from Typst's own layout, so every finding knows its
// page, paragraph and fragment. The PDF's boxes and fonts are read with `PDFFile` (the Mac uses CoreGraphics).
import { blockPlainText } from '@writing-suite/core';
import { resolvedMargins, pageInches } from '../manuscript.ts';
import type { PreflightFinding } from '../preflight.ts';
import type { CompiledManuscript, PrintSettings } from '../types.ts';
import {
  endsWithHyphen, hyphenated, lineWords, readable, type Metrics, type PrintLayout, type PrintLine,
} from './layout.ts';
import { asArray, asDict, asName, PDFFile, type PDFDict, type PDFValue } from './pdfFile.ts';
import { checkPDFX, PDFX_CONFORMANCE } from './pdfx.ts';
import type { Region } from './writer.ts';

/** A print-on-demand platform's interior specifications. */
export interface PrintPlatform {
  name: string;
  minPages: number;
  maxPages: number;
  /** Minimum inside margin by page count: the first row whose `pages` is at least the count. */
  gutter: { pages: number; inches: number }[];
  minOutside: number;
  minOutsideWithBleed: number;
  /** Whether the margins are a requirement (errors) or the printer's recommendation (warnings). */
  marginsRequired: boolean;
  /** The page count must be even (the printer pads an odd one with a blank page). */
  evenPages: boolean;
  /** The PDF standard the printer asks for, if any. */
  pdfStandard: string | null;
}

export const PRINT_PLATFORMS: Record<'kdp' | 'ingramSpark', PrintPlatform> = {
  /** Amazon KDP paperback, black ink on white paper. */
  kdp: {
    name: 'KDP', minPages: 24, maxPages: 828,
    gutter: [{ pages: 150, inches: 0.375 }, { pages: 300, inches: 0.5 }, { pages: 500, inches: 0.625 }, { pages: 700, inches: 0.75 }, { pages: 828, inches: 0.875 }],
    minOutside: 0.25, minOutsideWithBleed: 0.375, marginsRequired: true, evenPages: false, pdfStandard: null,
  },
  /** IngramSpark paperback, black and white: 0.5 in margins recommended, an even page count, PDF/X-1a:2001. */
  ingramSpark: {
    name: 'IngramSpark', minPages: 18, maxPages: 1200, gutter: [{ pages: 1200, inches: 0.5 }],
    minOutside: 0.5, minOutsideWithBleed: 0.5, marginsRequired: false, evenPages: true, pdfStandard: PDFX_CONFORMANCE,
  },
};

/** `PrintPlatform.named`. */
export function printPlatform(id: string): PrintPlatform | null {
  switch (id.toLowerCase()) {
    case 'kdp': return PRINT_PLATFORMS.kdp;
    case 'ingramspark': case 'ingram': case 'lightning-source': return PRINT_PLATFORMS.ingramSpark;
    default: return null;
  }
}

export function gutterFor(platform: PrintPlatform, count: number): number {
  return (platform.gutter.find((g) => count <= g.pages) ?? platform.gutter[platform.gutter.length - 1])?.inches ?? 0;
}

/** Families the web sets with a bundled stand-in (@writing-suite/typst's fonts.ts): the preset's font is "there". */
const STAND_INS: Record<string, string> = {
  'times new roman': 'liberation serif', times: 'liberation serif', palatino: 'tex gyre pagella',
  'palatino linotype': 'tex gyre pagella', 'book antiqua': 'tex gyre pagella',
};

export interface PrintOutput {
  /** The PDF. */
  data: Uint8Array;
  manuscript: CompiledManuscript;
  layout: PrintLayout;
}

/** `PrintPreflight.check`. */
export function printPreflight(result: PrintOutput): PreflightFinding[] {
  const print = result.manuscript.preset.render.print;
  if (!print) return [];
  const a = new Analysis(result.layout, result.manuscript);
  let f = [...a.widowsAndOrphans(), ...a.runts(), ...a.ladders(), ...a.spreads(), ...a.badBreaks(), ...a.sceneBreaks(print)];
  if (result.manuscript.preset.render.justify) f = [...f, ...a.rivers()];
  if (print.chapterOpensRecto) f = [...f, ...a.openers()];
  return [...f, ...specifications(result, a)];
}

const finding = (severity: PreflightFinding['severity'], code: string, message: string, extra: Partial<PreflightFinding> = {}): PreflightFinding => {
  const out: PreflightFinding = { severity, code, message };
  for (const [k, v] of Object.entries(extra)) if (v !== undefined && v !== null) (out as unknown as Record<string, unknown>)[k] = v;
  return out;
};

/** `inches`: `value.formatted(.number.precision(.fractionLength(0 ... 3)))`, which rounds half to even. */
const inchFormat = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 3, roundingMode: 'halfEven' } as Intl.NumberFormatOptions);
export const inches = (value: number) => inchFormat.format(value);

/** Subset fonts are named "ABCDEF+Family-Style": the family. */
export function cleanFontName(name: string): string {
  const plus = name.indexOf('+');
  const base = plus >= 0 ? name.slice(plus + 1) : name;
  return base.split('-').filter((p) => p !== '')[0] ?? base;
}

function mostCommon(values: string[]): string | null {
  const counts = new Map<string, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  let best: string | null = null, n = 0;
  for (const [v, c] of counts) if (c > n) { best = v; n = c; }
  return best;
}

function specifications(result: PrintOutput, a: Analysis): PreflightFinding[] {
  const f: PreflightFinding[] = [];
  const r = result.manuscript.preset.render;
  const print = r.print;
  if (!print) return f;
  const layout = result.layout;
  const { width: w, height: h } = pageInches(r.page.size);
  const pages = layout.pageCount;

  // Trim: every page the preset's size.
  const wrong = layout.pageSizes.map((s, i) => ({ s, i })).filter(({ s }) => Math.abs(s.width - w * 72) > 0.5 || Math.abs(s.height - h * 72) > 0.5);
  if (wrong.length) {
    f.push(finding('error', 'print.trim', `${wrong.length} page(s) aren't ${inches(w)} × ${inches(h)} in, starting with page ${wrong[0].i + 1}.`, { page: wrong[0].i + 1 }));
  }
  const pdf = readPDF(result.data);
  if (pdf) {
    const b = print.bleed * 72;
    const expected = [w * 72 + b, h * 72 + 2 * b];
    if (pdf.boxes.some((box) => Math.abs(box.width - expected[0]) > 0.5 || Math.abs(box.height - expected[1]) > 0.5)) {
      f.push(finding('error', 'print.bleed', print.bleed > 0
        ? `The PDF's pages don't include the ${inches(print.bleed)} in bleed.`
        : 'The PDF\'s pages are larger than the trim size, but the preset has no bleed.'));
    }
    if (print.bleed > 0 && pdf.boxes.some((box) => !box.hasTrimBox)) {
      f.push(finding('error', 'print.trim-box', 'Pages with bleed need a trim box so the printer knows where to cut.'));
    }
    // Fonts: every one embedded.
    const missing = [...pdf.fonts.entries()].filter(([, embedded]) => !embedded).map(([name]) => name).sort();
    if (missing.length) {
      f.push(finding('error', 'print.fonts', `Not embedded: ${missing.join(', ')}. Printers need every font in the file.`));
    } else {
      const names = [...new Set([...pdf.fonts.keys()].map(cleanFontName))].sort();
      f.push(finding('info', 'print.fonts-ok', `All fonts are embedded (${names.join(', ')}).`));
    }
  }
  // The body set in a fallback because the preset's font isn't available.
  const body = a.paragraphLines.map((i) => a.lines[i].font);
  const family = r.font.family.toLowerCase();
  const standIn = STAND_INS[family];
  const other = body.filter((b) => {
    const font = b.toLowerCase();
    return !font.includes(family) && !family.includes(font) && font !== standIn;
  });
  const used = mostCommon(other);
  if (body.length && other.length * 2 > body.length && used !== null) {
    f.push(finding('warning', 'print.font-fallback', `“${r.font.family}” isn't available on this device, so the text is set in ${used}. Install the font or choose another in the preset.`));
  }
  // Platform: page count, gutter and margins.
  const platform = printPlatform(print.platform);
  if (platform) {
    if (pages < platform.minPages) {
      f.push(finding('error', 'print.page-count', `${pages} pages is under ${platform.name}'s minimum of ${platform.minPages}.`));
    } else if (pages > platform.maxPages) {
      f.push(finding('error', 'print.page-count', `${pages} pages is over ${platform.name}'s maximum of ${platform.maxPages} for this paper.`));
    }
    const m = resolvedMargins(r.page);
    const gutter = gutterFor(platform, pages);
    const severity = platform.marginsRequired ? 'error' : 'warning';
    const needs = platform.marginsRequired ? 'needs at least' : 'recommends at least';
    if (m.inside + 0.0001 < gutter) {
      f.push(finding(severity, 'print.gutter', `The inside margin is ${inches(m.inside)} in; ${platform.name} ${needs} ${inches(gutter)} in for ${pages} pages.`));
    }
    const minimum = print.bleed > 0 ? platform.minOutsideWithBleed : platform.minOutside;
    for (const [edge, value] of [['outside', m.outside], ['top', m.top], ['bottom', m.bottom]] as const) {
      if (value + 0.0001 < minimum) f.push(finding(severity, 'print.margins', `The ${edge} margin is ${inches(value)} in; ${platform.name} ${needs} ${inches(minimum)} in.`));
    }
    if (platform.evenPages && pages % 2 === 1) {
      f.push(finding('info', 'print.even', `${pages} pages: ${platform.name} adds a blank page to make the count even.`));
    }
    if (platform.pdfStandard !== null && print.pdfx === undefined) {
      f.push(finding('warning', 'print.pdfx', `${platform.name} asks for ${platform.pdfStandard}; this preset makes an ordinary PDF. Use the IngramSpark print interior.`));
    }
  }
  // The PDF/X file itself (the preset asked for it).
  if (print.pdfx !== undefined) f.push(...checkPDFX(result.data));
  if (print.signature > 0 && pages % print.signature !== 0) {
    const blanks = print.signature - (pages % print.signature);
    f.push(finding('info', 'print.signature', `${pages} pages leaves ${blanks} blank page(s) at the end of the last ${print.signature}-page signature.`));
  }
  const named = printPlatform(print.platform);
  f.push(finding('info', 'print.summary', `${inches(w)} × ${inches(h)} in, ${pages} pages${named ? `, checked against ${named.name}` : ''}.`));
  return f;
}

// MARK: The PDF's boxes and fonts

/** Each page's MediaBox size and whether it has a TrimBox; every font by base name and whether it's embedded. Null
 *  when the file can't be read (as CoreGraphics returning nil). */
export function readPDF(data: Uint8Array): { boxes: { width: number; height: number; hasTrimBox: boolean }[]; fonts: Map<string, boolean> } | null {
  let file: PDFFile;
  let pages: ReturnType<PDFFile['pages']>;
  try {
    file = new PDFFile(data, { streams: true });
    pages = file.pages();
  } catch {
    return null;
  }
  if (!pages.length) return null;
  const resolve = (v: PDFValue | null | undefined) => { try { return file.resolve(v); } catch { return null; } };
  const boxes = pages.map((p) => ({
    width: Math.abs(p.mediaBox[2] - p.mediaBox[0]), height: Math.abs(p.mediaBox[3] - p.mediaBox[1]),
    hasTrimBox: asArray(resolve(p.dict.get('TrimBox'))) !== null,
  }));
  const fonts = new Map<string, boolean>();
  const seen = new Set<PDFDict>();
  const scan = (resources: PDFDict, depth: number) => {
    if (depth >= 8 || seen.has(resources)) return;
    seen.add(resources);
    const fontDict = asDict(resolve(resources.get('Font')));
    if (fontDict) {
      for (const [, entry] of fontDict.pairs) {
        const font = asDict(resolve(entry));
        if (!font) continue;
        const [name, embedded] = describeFont(font, resolve);
        fonts.set(name, (fonts.get(name) ?? true) && embedded);
      }
    }
    const xobjects = asDict(resolve(resources.get('XObject')));
    if (xobjects) {
      for (const [, entry] of xobjects.pairs) {
        const x = asDict(resolve(entry));
        const inner = x ? asDict(resolve(x.get('Resources'))) : null;
        if (inner) scan(inner, depth + 1);
      }
    }
  };
  for (const page of pages) {
    const resources = asDict(resolve(page.dict.get('Resources')));
    if (resources) scan(resources, 0);
  }
  return { boxes, fonts };
}

function describeFont(font: PDFDict, resolve: (v: PDFValue | null | undefined) => PDFValue | null): [string, boolean] {
  const name = asName(font.get('BaseFont')) ?? 'Type 3 font';
  if (asName(font.get('Subtype')) === 'Type3') return [name, true]; // glyphs are drawn in the file itself
  let owner = font;
  const descendants = asArray(resolve(font.get('DescendantFonts')));
  if (descendants?.length) {
    const first = asDict(resolve(descendants[0]));
    if (first) owner = first;
  }
  const descriptor = asDict(resolve(owner.get('FontDescriptor')));
  if (!descriptor) return [name, false];
  return [name, ['FontFile', 'FontFile2', 'FontFile3'].some((k) => descriptor.has(k))];
}

// MARK: Layout analysis

class Analysis {
  readonly lines: PrintLine[];
  /** Line indices of each region. */
  readonly byRegion: Map<number, number[]>;
  /** Indices of lines that belong to paragraphs. */
  readonly paragraphLines: number[];
  readonly metrics: Metrics;
  readonly measure: number;
  readonly lineHeight: number;
  readonly wordWidth: number;
  /** The physical page (0-based) numbered 1: the first chapter or part opener. */
  readonly firstNumbered: number;

  constructor(readonly layout: PrintLayout, readonly m: CompiledManuscript) {
    this.lines = layout.lines;
    this.byRegion = layout.linesByRegion();
    this.paragraphLines = layout.lines.map((_, i) => i).filter((i) => {
      const r = layout.lines[i].region;
      return r !== null && layout.regions[r].kind.type === 'paragraph';
    });
    this.metrics = layout.metrics();
    this.measure = this.metrics.measure;
    this.lineHeight = this.metrics.lineHeight;
    this.wordWidth = this.metrics.wordWidth;
    this.firstNumbered = layout.lines.find((line) => {
      if (line.region === null || layout.regions[line.region].kind.type !== 'heading') return false;
      return m.sections[layout.regions[line.region].section].role !== 'front-matter';
    })?.page ?? 0;
  }

  static median(values: number[]): number | null {
    return values.length ? [...values].sort((x, y) => x - y)[Math.floor(values.length / 2)] : null;
  }

  /** How a page is named in messages: its printed folio, and the PDF page when they differ. */
  name(page: number): string {
    const folio = page - this.firstNumbered + 1;
    return folio >= 1 && folio !== page + 1 ? `page ${folio} (PDF page ${page + 1})` : `page ${page + 1}`;
  }

  region(line: PrintLine): Region | null { return line.region !== null ? this.layout.regions[line.region] : null; }
  fragment(region: Region): string | null { return this.m.sections[region.section].fragmentID; }
  paragraphText(region: Region): string | null {
    const b = region.block;
    const blocks = this.m.sections[region.section].blocks;
    return b !== null && b < blocks.length ? blockPlainText(blocks[b]) : null;
  }

  /** A layout finding on a line, anchored to it (or `quote`) for a margin note. */
  finding(severity: PreflightFinding['severity'], code: string, message: string, line: PrintLine, quote?: string, copyfit?: string): PreflightFinding {
    const region = this.region(line);
    return finding(severity, code, message, {
      fragmentID: region ? this.fragment(region) ?? undefined : undefined, page: line.page + 1,
      quote: quote ?? readable(line), context: region ? this.paragraphText(region) ?? undefined : undefined, copyfit,
    });
  }

  /** Words that would fill `width` points of line. */
  words(width: number): number { return Math.max(1, Math.ceil(width / this.wordWidth)); }
  plural(n: number, word: string): string { return n === 1 ? `1 ${word}` : `${n} ${word}s`; }

  private paragraphs(minimum = 0): [number, number[]][] {
    return [...this.byRegion.entries()].filter(([r, indices]) => this.layout.regions[r].kind.type === 'paragraph' && indices.length >= minimum);
  }

  widowsAndOrphans(): PreflightFinding[] {
    const f: PreflightFinding[] = [];
    for (const [, indices] of this.paragraphs(2)) {
      const last = this.lines[indices[indices.length - 1]], before = this.lines[indices[indices.length - 2]];
      if (last.page !== before.page) {
        const cut = this.words(last.width), add = this.words(this.measure - last.width) + 1;
        f.push(this.finding('warning', 'print.widow', `The last line of a paragraph is alone at the top of ${this.name(last.page)}.`, last, undefined,
          `Cutting about ${this.plural(cut, 'word')} from this paragraph pulls its last line back onto the page before; adding about ${add} carries a second line over.`));
      }
      const first = this.lines[indices[0]], second = this.lines[indices[1]];
      if (first.page !== second.page) {
        let copyfit: string | undefined;
        let anchor = first;
        // Room comes from the paragraph before, if it ends on this page.
        if (indices[0] > 0) {
          const previous = this.lines[indices[0] - 1];
          const p = previous.region;
          if (previous.page === first.page && p !== null && this.layout.regions[p].kind.type === 'paragraph'
            && this.byRegion.get(p)?.at(-1) === indices[0] - 1) {
            anchor = previous;
            const cut = lineWords(previous), add = this.words(this.measure - previous.width) + 1;
            copyfit = `Cutting about ${this.plural(cut, 'word')} from this paragraph frees a line on that page for the next paragraph's second line; adding about ${add} pushes the next paragraph's first line over to the next page.`;
          }
        }
        const found = this.finding('warning', 'print.orphan', `The first line of a paragraph is alone at the foot of ${this.name(first.page)}.`, first);
        if (copyfit !== undefined) {
          found.copyfit = copyfit;
          found.quote = readable(anchor);
          const region = this.region(anchor);
          const context = region ? this.paragraphText(region) : null;
          if (context !== null) found.context = context; else delete found.context;
          const fragment = region ? this.fragment(region) : null;
          if (fragment !== null) found.fragmentID = fragment; else delete found.fragmentID;
        }
        f.push(found);
      }
    }
    return f;
  }

  runts(): PreflightFinding[] {
    const f: PreflightFinding[] = [];
    for (const [, indices] of this.paragraphs(2)) {
      const last = this.lines[indices[indices.length - 1]];
      const word = readable(last);
      const tail = hyphenated(this.lines[indices[indices.length - 2]]);
      if (!(lineWords(last) === 1 && (tail || [...word].filter((c) => /\p{L}/u.test(c)).length <= 3))) continue;
      const add = this.words(this.measure / 4 - last.width);
      f.push(this.finding('info', 'print.runt', `${tail ? `The end of a broken word, “${word}”,` : `“${word}”`} is alone on the last line of a paragraph on ${this.name(last.page)}.`,
        this.lines[indices[indices.length - 2]], undefined,
        `Cutting a word from this paragraph pulls “${word}” up to the line above; adding about ${this.plural(add, 'word')} gives it company.`));
    }
    return f;
  }

  ladders(): PreflightFinding[] {
    const f: PreflightFinding[] = [];
    for (const [, indices] of this.paragraphs()) {
      let run: number[] = [];
      for (const i of [...indices.slice(0, -1), -1]) {
        if (i >= 0 && endsWithHyphen(this.lines[i])) { run.push(i); continue; }
        if (run.length >= 3) {
          const first = this.lines[run[0]];
          f.push(this.finding('warning', 'print.ladder', `${run.length} lines in a row end in a hyphen on ${this.name(first.page)}.`, first));
        }
        run = [];
      }
    }
    return f;
  }

  /** Chains of unusually wide word spaces that line up down four or more justified lines. */
  rivers(): PreflightFinding[] {
    const f: PreflightFinding[] = [];
    const byPage = new Map<number, number[]>();
    for (const i of this.paragraphLines) {
      const page = this.lines[i].page;
      const list = byPage.get(page);
      if (list) list.push(i); else byPage.set(page, [i]);
    }
    const medians = new Map<number, number | null>();
    for (const [page, group] of byPage) medians.set(page, Analysis.median(group.flatMap((i) => this.lines[i].gaps.map((g) => g.width))));
    for (const [, indices] of this.paragraphs(5)) {
      let previous: { x: number; width: number; length: number }[] = [];
      let longest = 0, at = indices[0];
      for (const i of indices.slice(0, -1)) {
        const line = this.lines[i];
        const median = medians.get(line.page);
        if (median === undefined || median === null) continue;
        const wide = line.gaps.filter((g) => g.width > median * 1.6 && g.width > line.size * 0.4);
        const current = wide.map((gap) => {
          const lengths = previous.filter((p) => Math.abs((p.x + p.width / 2) - (gap.x + gap.width / 2)) <= (p.width + gap.width) / 2).map((p) => p.length);
          return { x: gap.x, width: gap.width, length: 1 + (lengths.length ? Math.max(...lengths) : 0) };
        });
        const best = current.length ? Math.max(...current.map((c) => c.length)) : null;
        if (best !== null && best > longest) { longest = best; at = i; }
        previous = i + 1 < this.lines.length && this.lines[i + 1].page === line.page ? current : [];
      }
      if (longest >= 4) {
        f.push(this.finding('info', 'print.river', `Wide word spaces line up down ${longest} lines on ${this.name(this.lines[at].page)}, forming a river.`, this.lines[at]));
      }
    }
    return f;
  }

  /** Facing pages whose last lines don't align. The foot of a chapter doesn't count. */
  spreads(): PreflightFinding[] {
    return this.layout.imbalancedSpreads(this.metrics).map((spread) => {
      const short = spread.short, count = spread.lines;
      const line = this.lines[spread.lastLine];
      let copyfit: string | undefined;
      const p = line.region;
      const indices = p !== null ? this.byRegion.get(p) : undefined;
      if (count === 1 && p !== null && this.layout.regions[p].kind.type === 'paragraph' && indices) {
        const last = this.lines[indices[indices.length - 1]];
        const add = this.words(this.measure - last.width) + 1;
        copyfit = last.page > short
          ? `Adding about ${add} words to this paragraph, so it runs a line longer, lets the page fill.`
          : `Adding about ${add} words to this paragraph, or cutting about ${this.plural(lineWords(last), 'word')} so it loses its last line, lets the page fill.`;
      }
      const name = this.name(short);
      return this.finding('warning', 'print.spread', `${name.slice(0, 1).toUpperCase() + name.slice(1)} ends ${this.plural(count, 'line')} short of the page facing it.`,
        line, undefined, copyfit);
    });
  }

  openers(): PreflightFinding[] {
    const f: PreflightFinding[] = [];
    for (const [r, indices] of this.byRegion) {
      if (this.layout.regions[r].kind.type !== 'heading') continue;
      const section = this.m.sections[this.layout.regions[r].section];
      if (!(section.role === 'chapter' || section.role === 'part') || !indices.length) continue;
      const line = this.lines[indices[0]];
      if ((line.page + 1) % 2 === 0) {
        f.push(finding('error', 'print.opener', `“${readable(line)}” opens on ${this.name(line.page)}, a left-hand page; the preset opens ${section.role === 'part' ? 'parts' : 'chapters'} on the right.`,
          { fragmentID: section.fragmentID ?? undefined, page: line.page + 1 }));
      }
    }
    return f;
  }

  badBreaks(): PreflightFinding[] {
    const f: PreflightFinding[] = [];
    const body = this.paragraphLines;
    body.forEach((i, n) => {
      const line = this.lines[i];
      if (!endsWithHyphen(line) || n + 1 >= body.length) return;
      const next = this.lines[body[n + 1]];
      const words = readable(line).split(' ').filter((w) => w !== '');
      const part = words.at(-1);
      if (hyphenated(line) && part !== undefined && /^\p{Uppercase}/u.test(part)) {
        const before = words.length >= 2 ? words[words.length - 2] : undefined;
        const sentenceStart = before !== undefined
          ? (before.length ? '.?!:;…"”’'.includes([...before].at(-1)!) : false)
          : this.byRegion.get(line.region ?? -1)?.[0] === i;
        if (!sentenceStart) {
          const rest = readable(next).split(' ').filter((w) => w !== '')[0] ?? '';
          const word = (part.endsWith('-') ? part.slice(0, -1) : part) + trimPunctuation(rest);
          f.push(this.finding('warning', 'print.bad-break', `“${word}” is hyphenated on ${this.name(line.page)}; names shouldn't be broken.`, line));
        }
      }
      // Recto pages are odd; the reader turns the page to finish the word.
      if (next.page > line.page && (line.page + 1) % 2 === 1) {
        f.push(this.finding('warning', 'print.page-turn', `A word is split across the page turn after ${this.name(line.page)}.`, line));
      }
    });
    return f;
  }

  sceneBreaks(print: PrintSettings): PreflightFinding[] {
    const f: PreflightFinding[] = [];
    const layout = this.layout;
    if (layout.loosenedParagraphs > 0) {
      f.push(finding('info', 'print.balanced', `${this.plural(layout.loosenedParagraphs, 'paragraph')} ${layout.loosenedParagraphs === 1 ? 'was' : 'were'} tracked slightly looser so facing pages align.`));
    }
    if (layout.ornamentedBreaks > 0) {
      f.push(finding('info', 'print.scene-break-fixed', `${this.plural(layout.ornamentedBreaks, 'blank scene break')} fell at a page edge, so ${layout.ornamentedBreaks === 1 ? "it's" : "they're"} marked with ${print.pageEdgeOrnament}.`));
    }
    const invisible = new Set(layout.invisibleBreaks());
    for (const region of layout.regions) {
      if (region.kind.type !== 'sceneBreak' || !invisible.has(region.kind.index)) continue;
      const page = [...this.lines].reverse().find((l) => l.region !== null && layout.regions[l.region].start < region.start)?.page;
      f.push(finding('error', 'print.invisible-break', `A blank-line scene break falls at the foot of ${this.name(page ?? 0)}, where readers can't see it. Set an ornament for page edges in the preset.`,
        { fragmentID: this.fragment(region) ?? undefined, page: page !== undefined ? page + 1 : undefined }));
    }
    return f;
  }
}

/** `trimmingCharacters(in: .punctuationCharacters)`. */
const trimPunctuation = (s: string) => s.replace(/^\p{P}+|\p{P}+$/gu, '');
